import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { SupClass } from "../api/models/supClass.models";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { getClassLabelFromAkeneoObject } from "../utils/akeneoUtils";

const Row = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;

  & > div {
    flex: 1;
    min-width: 200px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 1rem;

  & .p-dropdown {
    width: 100%;
    font-size: 0.9rem;
  }

  & label {
    font-size: 0.9rem;
    white-space: nowrap;
  }
`;

const StyledDropdown = styled(Dropdown)`
  width: 100%;

  & .p-inputtext {
    font-size: 0.9rem;
  }
`;

interface SupSelectionProps {
  isSup: boolean;
  onIsSupChange: (checked: boolean) => void;
  supClass?: string;
  onSupClassChange: (code?: string) => void;
  supClasses?: SupClass[];
  disabled?: boolean;
}

const SupSelection: React.FC<SupSelectionProps> = ({
  isSup,
  onIsSupChange,
  supClass,
  onSupClassChange,
  supClasses,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [locale] = useUIStore(useShallow((state: UIState) => [state.locale]));

  const supOptions = supClasses?.map((category) => ({
    label: getClassLabelFromAkeneoObject(locale, category),
    value: category.code,
  }));

  return (
    <Row>
      <InputContainer style={{ flex: "0 0 280px" }}>
        {/* hidden label keeps the checkbox vertically aligned with the dropdown's label + control */}
        <label style={{ visibility: "hidden" }}>{t("header-sup-class")}</label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            height: "100%",
          }}
        >
          <Checkbox
            inputId="isSup"
            checked={isSup}
            onChange={(e) => {
              onIsSupChange(!!e.checked);
              if (!e.checked) {
                onSupClassChange(undefined);
              }
            }}
            disabled={disabled}
          />
          <label htmlFor="isSup">{t("label-single-use-plastic")}</label>
        </div>
      </InputContainer>
      <InputContainer style={{ flex: "1" }}>
        <label>{t("header-sup-class")}</label>
        <StyledDropdown
          disabled={disabled || !isSup}
          value={supClass}
          options={supOptions}
          onChange={(e) => onSupClassChange(e.value)}
          placeholder={t("label-select-sup-class")}
          showClear
        />
      </InputContainer>
    </Row>
  );
};

export default SupSelection;
